import { getApiUserManagementV1Databases } from '@/libs/api-user-mgt/generated/databases/databases';
import { axiosInstance } from '@/libs/api-user-mgt/axios-instance';
import { getApiUserManagementV1Features } from '@/libs/api-user-mgt/generated/features/features';
import { putApiUserManagementV1FeaturesSensitivity } from '@/libs/api-user-mgt/umOpenApiGaps/featuresSensitivityNotInOpenApiClient';
import {
  getApiUserManagementV1SettingsChangeCategories,
  getApiUserManagementV1SettingsChangeCategoriesActive,
  putApiUserManagementV1SettingsChangeCategories
} from '@/libs/api-user-mgt/umOpenApiGaps/changeCategoriesNotInOpenApiClient';
import {
  getApiUserManagementV1Settings,
  putApiUserManagementV1Settings
} from '@/libs/api-user-mgt/umOpenApiGaps/settingsNotInOpenApiClient';

import type {
  ChangeCategoryListDto,
  DatabaseRecord,
  UpdateChangeCategoriesInput,
  UpdateFeatureSensitivityInput,
  UpdateTenantSettingsInput
} from '@/userManagement/application/types/UserManagementApiContracts';
import type { Feature } from '@/userManagement/domain/entities/Feature';
import type { TenantSettings } from '@/userManagement/domain/entities/TenantSettings';

import { normalizeChangeCategoryList } from './changeCategoriesWire';
import { normalizeFeaturesResponse } from './featuresWire';
import { normalizeTenantSettings } from './tenantSettingsWire';

/** Matches BE `DatabaseListScope.UsersTabFilter`. */
export const USERS_TAB_DATABASE_LIST_SCOPE = 'usersTabFilter';

/** Matches BE `DatabaseListScope.ActiveCatalog` (TROC-363 Add User). */
export const ACTIVE_CATALOG_DATABASE_LIST_SCOPE = 'activeCatalog';

function parseDatabaseList(data: unknown): DatabaseRecord[] {
  const list = Array.isArray(data) ? (data as Array<Record<string, unknown>>) : [];
  return list
    .filter((row): row is Record<string, unknown> => row != null && typeof row === 'object')
    .map((row, index) => {
      const nameRaw = row.name ?? row.Name;
      const name = typeof nameRaw === 'string' ? nameRaw.trim() : '';
      if (name === '') return null;
      const aliasRaw = row.alias ?? row.Alias ?? row.description ?? row.Description ?? null;
      const alias = typeof aliasRaw === 'string' && aliasRaw.trim() !== '' ? aliasRaw.trim() : null;
      return {
        id: index + 1,
        name,
        description: alias,
        alias
      };
    })
    .filter((row): row is DatabaseRecord => row != null);
}

export const userManagementCatalogSegment = {
  /**
   * Full tenant database catalog (`CrossDatabaseInfo`, all `IsTenantDatabase` rows).
   * Used for alias lookups and surfaces that need every org tenant DB — not Add User (TROC-363).
   */
  getDatabases: async (): Promise<DatabaseRecord[]> => {
    const r = await getApiUserManagementV1Databases();
    return parseDatabaseList(r.data);
  },

  /**
   * TROC-211: Users-tab Databases filter — allowlisted DBs (ClientDeploymentControl) with ≥1 synced user.
   * Uses `GET /databases?scope=usersTabFilter` until OpenAPI codegen includes the scope query param.
   */
  getDatabasesForUsersTabFilter: async (): Promise<DatabaseRecord[]> => {
    const r = await axiosInstance<unknown>(
      {
        url: '/api/user-management/v1/databases',
        method: 'GET',
        params: { scope: USERS_TAB_DATABASE_LIST_SCOPE }
      },
      undefined
    );
    return parseDatabaseList(r.data);
  },

  /**
   * TROC-363: Add User active catalog — CDC-allowlisted tenant DBs including empty DBs.
   * Uses `GET /databases?scope=activeCatalog`.
   */
  getDatabasesForActiveCatalog: async (): Promise<DatabaseRecord[]> => {
    const r = await axiosInstance<unknown>(
      {
        url: '/api/user-management/v1/databases',
        method: 'GET',
        params: { scope: ACTIVE_CATALOG_DATABASE_LIST_SCOPE }
      },
      undefined
    );
    return parseDatabaseList(r.data);
  },

  getFeatures: async (): Promise<Feature[]> => {
    const r = await getApiUserManagementV1Features();
    return normalizeFeaturesResponse(r.data);
  },

  getTenantSettings: async (): Promise<TenantSettings> => {
    const r = await getApiUserManagementV1Settings();
    const data = r.data as unknown;
    if (Array.isArray(data)) {
      const settings = data[0];
      if (!settings) throw new Error('Tenant settings not configured');
      return normalizeTenantSettings(settings as Record<string, unknown>);
    }
    if (data && typeof data === 'object') {
      return normalizeTenantSettings(data as Record<string, unknown>);
    }
    throw new Error('Tenant settings not configured');
  },

  updateTenantSettings: async (data: UpdateTenantSettingsInput): Promise<TenantSettings> => {
    const r = await putApiUserManagementV1Settings(data);
    const payload = r.data as unknown;
    if (payload && typeof payload === 'object') {
      return normalizeTenantSettings(payload as Record<string, unknown>);
    }
    throw new Error('Tenant settings update returned invalid payload');
  },

  /** TROC-243: org-scoped sensitivity overrides. Returns effective catalog like GET /features. */
  updateFeatureSensitivity: async (input: UpdateFeatureSensitivityInput): Promise<Feature[]> => {
    const r = await putApiUserManagementV1FeaturesSensitivity(input);
    return normalizeFeaturesResponse(r.data);
  },

  /** TROC-299: full org change-category list (admin editor; includes inactive). */
  getChangeCategories: async (): Promise<ChangeCategoryListDto> => {
    const r = await getApiUserManagementV1SettingsChangeCategories();
    return normalizeChangeCategoryList(r.data);
  },

  /** TROC-299: active change categories for reason pickers. */
  getActiveChangeCategories: async (): Promise<ChangeCategoryListDto> => {
    const r = await getApiUserManagementV1SettingsChangeCategoriesActive();
    return normalizeChangeCategoryList(r.data);
  },

  /** TROC-299: replace org change-category list; returns full list. */
  updateChangeCategories: async (input: UpdateChangeCategoriesInput): Promise<ChangeCategoryListDto> => {
    const r = await putApiUserManagementV1SettingsChangeCategories(input);
    return normalizeChangeCategoryList(r.data);
  }
};
